/**
 * ImportWizard
 */

import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Modal } from 'react-bootstrap';
import ImportSheet from 'components/interaction/ImportSheet/index.jsx';
import MatchColumns from 'components/interaction/ImportSheet/MatchColumns.jsx';
import PrimaryAction from 'components/interaction/PrimaryAction/index.jsx';
import useWizard from 'utils/useWizard.jsx';
import { intlLink } from 'utils/intlFunctions.jsx';
import messages from './messages.js';
import mapData from './mapData.js';

function ImportWizard(props) {
  const { show, onHide, title, intro = messages.selectFileIntro, columns, template, showConfirmSubmit } = props;
  const intl = useIntl();
  const [data, setData] = useState(null);
  const [map, setMap] = useState(null);
  const hasData = data?.rows.length > 0;
  const mappedData = useMemo(() => hasData && map && mapData(data, map), [hasData, data, map]);
  const steps = [
    { label: messages.selectFile },
    { label: messages.matchColumns, disabled: () => !hasData },
    { label: messages.confirmSubmit, disabled: () => !mappedData },
  ];
  const fields = useMemo(() => {
    const result = { list: columns };
    if (columns) {
      columns.forEach((c) => {
        result[c] = intl.formatMessage(messages[c]);
      });
    }
    return result;
  }, [columns]); // eslint-disable-line react-hooks/exhaustive-deps

  const { activeStep, StepNav, setActive } = useWizard(steps);
  useEffect(() => {
    if (show) {
      return;
    }
    setData(null);
    setMap(null);
    setActive(1);
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps
  const showAction = () => (
    <PrimaryAction
      onCancel={onHide}
      onSubmit={() => setActive(activeStep + 1)}
      valid={!steps[activeStep].disabled()}
      submitText={messages.next}
    />
  );
  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header>
        <Modal.Title>
          <FormattedMessage {...title} />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="mb-4">
        <StepNav />
        {activeStep === 1 && (
          <>
            <p className="form-text"><FormattedMessage {...intro} values={intlLink(template)} /></p>
            <ul className="mb-5 form-text">
              {fields.list.map((fld) => <li key={fld}>{fields[fld]}</li>)}
            </ul>
            <ImportSheet onChange={(d) => setData(d.sheetData)} />
            {data
              && <p className="py-3"><FormattedMessage {...messages.numSheetRows} values={{ count: data.rows.length }} /></p>}
            {showAction()}
          </>
        )}
        {activeStep === 2 && data && (
          <>
            <p className="form-text"><FormattedMessage {...messages.matchColumnsIntro} /></p>
            <MatchColumns fields={fields} columns={data.fields} value={map} onChange={setMap} />
            {showAction()}
          </>
        )}
        {activeStep === 3 && map && showConfirmSubmit(mappedData)}
      </Modal.Body>
    </Modal>
  );
}

ImportWizard.propTypes = {
  title: PropTypes.object.isRequired,
  intro: PropTypes.object,
  columns: PropTypes.array.isRequired,
  template: PropTypes.string.isRequired,
  showConfirmSubmit: PropTypes.func.isRequired,
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
};

export default ImportWizard;
